import React, { useState, useEffect, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  AppBar,
  Icon,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ErrorIcon from '@mui/icons-material/Error';
import { Trans } from 'react-i18next';

import AuthService from 'services/Auth';
import ConfirmExitPopup from 'components/ConfirmExitPopup/ConfirmExitPopup';
import './PasswordRecovery.scss';

const ModalToolbar = lazy(() => import('components/ModalToolbar'));
const EnvLabel = lazy(() => import('components/EnvLabel'));

const PasswordRecovery = () => {
  const [step, setStep] = useState(1);
  const [account, setAccount] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [passwordBackendError, setPasswordBackendError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1800);
  const [emailTouched, setEmailTouched] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [touched, setTouched] = useState({
    account: false,
    email: false,
    code: false,
    password: false,
    confirmPassword: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (step !== 2 || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [step, timeLeft]);

  const formatTime = (seconds) => {
    const min = String(Math.floor(seconds / 60)).padStart(2, '0');
    const sec = String(seconds % 60).padStart(2, '0');
    return `${min}:${sec}`;
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const throwStatusError = (status) => {
    const error = new Error('Server returned error status');
    error.status = status;
    throw error;
  };

  const handleResend = async () => {
    try {
      const result = await AuthService.requestPasswordReset({
        adAccount: account,
        recoveryEmail: email,
      });
      if (result.status === 'SUCCESS') {
        setError(null);
        setTimeLeft(1800);
      }
    } catch (err) {
      const status = err.code || err.status || err.message;
      const msg =
        err.message ||
        (status === 'LIMIT_REACHED'
          ? 'Too many attempts. Please try again later.'
          : 'Failed to resend code. Please try again later.');
      setError(msg);
    }
  };

  const handleSubmitStep1 = async () => {
    try {
      const result = await AuthService.requestPasswordReset({
        adAccount: account,
        recoveryEmail: email,
      });
      if (result.status === 'SUCCESS') {
        setError(null);
        setStep(2);
        setTimeLeft(1800);
      } else {
        throwStatusError(result.status);
      }
    } catch (err) {
      const status = err.code || err.status || err.message;

      switch (status) {
        case 'INVALID':
          setError('Account details or recovery email not found.');
          break;
        case 'LIMIT_REACHED':
          setError('Too many attempts. Please try again later.');
          break;
        default:
          setError('Account details or recovery email not found.');
      }
    }
  };

  const canSubmitStep1 = () => {
    const valid = account && email && isValidEmail(email);
    return valid && !error;
  };

  const handleSubmitStep2 = async () => {
    try {
      const result = await AuthService.verifyCode({
        adAccount: account,
        recoveryEmail: email,
        passwordResetCode: Number(code),
      });

      if (result.status === 'SUCCESS') {
        setError(null);
        setStep(3);
      } else {
        throw new Error(result.status);
      }
    } catch (err) {
      const status = err.code || err.status || err.message;

      switch (status) {
        case 'EXPIRED':
          setError(
            'Verification code has expired. Please resend the verification code to get a new one and try again.'
          );
          break;
        case 'INVALID':
          setError('Incorrect code. Please try again.');
          break;
        case 'LIMIT_REACHED':
          setError(
            'You have reached the maximum number of attempts. Please try again later.'
          );
          break;
        default:
          setError('Something went wrong. Please try again.');
      }
    }
  };

  const handleSubmitStep3 = async () => {
    if (password !== confirmPassword) return setError("Passwords don't match.");
    const { minLength, validTypes, noUsernameParts } =
      getPasswordValidationStatus(password, account, email);

    if (!minLength || !validTypes || !noUsernameParts) {
      return setError('Password does not meet requirements.');
    }

    try {
      await AuthService.resetPassword({
        adAccount: account,
        recoveryEmail: email,
        passwordResetCode: code,
        password,
      });
      setError(null);
      setSuccess(true);
      setPasswordBackendError(false);
    } catch (err) {
      const status = err.code || err.status || '';
      if (status === 'EXPIRED') {
        setError('Verification code expired. Please restart the process.');
      } else if (status === 'LIMIT_REACHED') {
        setError('Attempt limit reached. Please try again later.');
      } else if (status === 'INVALID') {
        setError(err.message || 'Password does not meet requirements.');
        setPasswordBackendError(
          err.message.includes('contain personal information')
        );
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    }
  };

  const canSubmitStep3 = () =>
    validationStatus.allValid && password === confirmPassword && !error;

  const handleFieldChange = (field, setter) => (e) => {
    setter(e.target.value);
    if (error) setError(null);
    if (passwordBackendError) setPasswordBackendError(false);
  };

  const getPasswordValidationStatus = (pwd, login, email) => {
    const minLength = pwd.length >= 14;
    const hasUppercase = /[A-Z]/.test(pwd);
    const hasLowercase = /[a-z]/.test(pwd);
    const hasNumber = /\d/.test(pwd);
    const hasSymbol = /[^A-Za-z0-9]/.test(pwd);
    const hasNoWhitespace = !!pwd.length && !/\s/.test(pwd);
    const passwordsMismatch =
      confirmPassword.length > 0 && confirmPassword !== password;

    const validTypes =
      [hasUppercase, hasLowercase, hasNumber, hasSymbol].filter(Boolean)
        .length >= 3;

    const lowerPwd = pwd.toLowerCase();
    const forbiddenValues = [login, email?.split('@')[0]]
      .filter(Boolean)
      .map((val) => val.trim().toLowerCase());

    const hasUsernameParts = forbiddenValues.some((v) => lowerPwd.includes(v));

    return {
      minLength,
      validTypes,
      hasUppercase,
      hasLowercase,
      hasNumber,
      hasSymbol,
      hasNoWhitespace,
      passwordsMismatch,
      noUsernameParts:
        lowerPwd.length >= 3 && !hasUsernameParts && !passwordBackendError,
      allValid:
        minLength &&
        validTypes &&
        !hasUsernameParts &&
        hasNoWhitespace &&
        !passwordsMismatch,
    };
  };

  const validationStatus = getPasswordValidationStatus(
    password,
    account,
    email
  );

  const onClose = () => {
    if (step <= 3 && !success) {
      setConfirmOpen(true);
    } else {
      navigate('/login');
    }
  };

  return (
    <>
      <EnvLabel isFixed={true} styles={{ zIndex: 1900 }} />
      <AppBar position="fixed">
        <ModalToolbar
          title={'Password Recovery'}
          iconName={'close'}
          callback={() => onClose()}
        ></ModalToolbar>
      </AppBar>
      <div className="recovery-container">
        <div className="step-group">
          {/* Step 1 */}
          <div
            className={`step-block ${step === 1 ? 'active' : step > 1 ? 'done' : ''}`}
          >
            <div className="step-header">
              <div
                className={`step-circle ${step === 1 ? 'active' : step > 1 ? 'done' : ''}`}
              >
                {step === 1 ? '1' : <Icon>done</Icon>}
              </div>
              <div
                className={`step-title ${step === 1 ? 'active' : step > 1 ? 'done' : ''}`}
              >
                Identify Yourself
              </div>
            </div>
            {step === 1 && (
              <div className="step-content">
                <Typography className="step-description">
                  Please enter your details below to recover your password:
                </Typography>
                {error && (
                  <Alert
                    className="error-notification"
                    severity="error"
                    icon={<ErrorIcon fontSize="inherit" />}
                  >
                    {error}
                  </Alert>
                )}
                <TextField
                  label="Aviapartner AD account"
                  fullWidth
                  required
                  value={account}
                  onChange={handleFieldChange('account', setAccount)}
                  error={!!error && touched.account}
                  margin="normal"
                />

                <TextField
                  label="Recovery email address"
                  fullWidth
                  required
                  value={email}
                  onChange={handleFieldChange('email', setEmail)}
                  onBlur={() => setEmailTouched(true)}
                  error={
                    !!(
                      (error && touched.email) ||
                      (emailTouched && email && !isValidEmail(email))
                    )
                  }
                  helperText={
                    emailTouched && email && !isValidEmail(email)
                      ? 'Please enter a valid email address'
                      : ''
                  }
                  margin="normal"
                />
                <Button
                  className="submit-button"
                  variant="contained"
                  fullWidth
                  disabled={!canSubmitStep1()}
                  onClick={handleSubmitStep1}
                >
                  SUBMIT
                </Button>
              </div>
            )}
          </div>

          {/* Step 2 */}
          <div
            className={`step-block ${step === 2 ? 'active' : step > 2 ? 'done' : ''}`}
          >
            <div className="step-header">
              <div
                className={`step-circle ${step === 2 ? 'active' : step > 2 ? 'done' : ''}`}
              >
                {step < 3 ? '2' : <Icon>done</Icon>}
              </div>
              <div
                className={`step-title ${step === 2 ? 'active' : step > 2 ? 'done' : ''}`}
              >
                Enter the Verification Code{' '}
                {step === 2 && (
                  <span style={{ marginLeft: 8 }}>
                    {timeLeft === 0 ? '00:00' : formatTime(timeLeft)}
                  </span>
                )}
              </div>
            </div>
            {step === 2 && (
              <div className="step-content">
                {error && (
                  <Alert
                    className="error-notification"
                    severity="error"
                    icon={<ErrorIcon fontSize="inherit" />}
                  >
                    {error}
                  </Alert>
                )}
                <Typography variant="body2" className="step-description">
                  We've sent an 8-digit code to your recovery email.
                </Typography>
                <TextField
                  type="number"
                  label="Verification Code"
                  fullWidth
                  required
                  value={code}
                  onChange={handleFieldChange('code', setCode)}
                  error={!!error && touched.code}
                  margin="normal"
                />
                <Typography
                  className="resend-label"
                  variant="body2"
                  sx={{ mt: 1 }}
                >
                  Didn't receive the email?{' '}
                  <Button
                    className="resend-button"
                    size="small"
                    onClick={handleResend}
                  >
                    Resend
                  </Button>
                </Typography>
                <Button
                  className="submit-button"
                  variant="contained"
                  fullWidth
                  disabled={code.length !== 8 || !!error}
                  onClick={handleSubmitStep2}
                >
                  SUBMIT
                </Button>
              </div>
            )}
          </div>

          {/* Step 3 */}
          <div
            className={`step-block ${step === 3 ? (success ? 'done' : 'active') : ''}`}
          >
            <div className="step-header">
              <div
                className={`step-circle ${step === 3 ? (success ? 'done' : 'active') : ''}`}
              >
                {step === 3 ? success ? <Icon>done</Icon> : '3' : '3'}
              </div>
              <div
                className={`step-title ${step === 3 ? (success ? 'done' : 'active') : ''}`}
              >
                Set a New Password
              </div>
            </div>
            {step === 3 && !success && (
              <div className="step-content">
                <>
                  <div className="password-rules">
                    <Typography
                      className="rule-item"
                      sx={{
                        color: validationStatus.minLength ? 'green' : 'inherit',
                      }}
                    >
                      ! Min. 14 characters
                    </Typography>
                    <Typography
                      className="rule-item"
                      sx={{
                        color: validationStatus.validTypes
                          ? 'green'
                          : 'inherit',
                      }}
                    >
                      ! 3 of 4 types required:
                    </Typography>
                    <ul className="rules-sublist">
                      <li
                        style={{
                          color: validationStatus.hasUppercase ? 'green' : '',
                        }}
                      >
                        uppercase
                      </li>
                      <li
                        style={{
                          color: validationStatus.hasLowercase ? 'green' : '',
                        }}
                      >
                        lowercase
                      </li>
                      <li
                        style={{
                          color: validationStatus.hasNumber ? 'green' : '',
                        }}
                      >
                        numbers
                      </li>
                      <li
                        style={{
                          color: validationStatus.hasSymbol ? 'green' : '',
                        }}
                      >
                        symbols
                      </li>
                    </ul>
                    <Typography
                      className="rule-item"
                      sx={{
                        color: validationStatus.noUsernameParts
                          ? 'green'
                          : 'inherit',
                      }}
                    >
                      ! No name/username parts
                    </Typography>
                    <Typography
                      className="rule-item"
                      sx={{
                        color: validationStatus.hasNoWhitespace
                          ? 'green'
                          : 'inherit',
                      }}
                    >
                      ! No spaces
                    </Typography>
                  </div>

                  {error && (
                    <Alert
                      className="error-notification"
                      severity="error"
                      icon={<ErrorIcon fontSize="inherit" />}
                    >
                      {error}
                    </Alert>
                  )}
                  <TextField
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    value={password}
                    onChange={handleFieldChange('password', setPassword)}
                    onBlur={() => setTouched((p) => ({ ...p, password: true }))}
                    error={!!error && touched.password}
                    margin="normal"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Confirm New Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    fullWidth
                    value={confirmPassword}
                    onChange={handleFieldChange(
                      'confirmPassword',
                      setConfirmPassword
                    )}
                    onBlur={() =>
                      setTouched((p) => ({ ...p, confirmPassword: true }))
                    }
                    error={
                      (!!error && touched.confirmPassword) ||
                      (touched.confirmPassword && confirmPassword !== password)
                    }
                    helperText={
                      touched.confirmPassword &&
                      confirmPassword !== password ? (
                        <Trans>Passwords do not match</Trans>
                      ) : (
                        ' '
                      )
                    }
                    margin="normal"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    className="submit-button"
                    variant="contained"
                    fullWidth
                    disabled={!canSubmitStep3()}
                    onClick={handleSubmitStep3}
                  >
                    SUBMIT
                  </Button>
                </>
              </div>
            )}
          </div>
        </div>
        {step === 3 && success && (
          <div className="success-footer">
            <Typography variant="body2" className="success-text">
              <span style={{ color: 'green', fontWeight: 700 }}>Success!</span>{' '}
              Your password has been updated. You're all set to sign in with
              your new credentials.
            </Typography>
            <Button
              className="success-button"
              variant="outlined"
              fullWidth
              href="/login"
            >
              GO TO LOGIN
            </Button>
          </div>
        )}
        {confirmOpen && (
          <ConfirmExitPopup
            onCancel={() => setConfirmOpen(false)}
            onClose={() => navigate('/login')}
          />
        )}
      </div>
    </>
  );
};

export default PasswordRecovery;
